import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/auth';
import api from '../services/api';
import CopyButton from '../components/CopyButton';
import SmartLinkModal from '../components/SmartLinkModal';

interface SmartLink {
  id: string;
  name: string;
  shortCode: string;
  targetUrl: string;
  campaign?: string;
  source?: string;
  medium?: string;
  clicks: number;
  conversions: number;
  isActive: boolean;
  createdAt: string;
}

interface SmartLinkFormData {
  name: string;
  targetUrl: string;
  campaign?: string;
  source?: string;
  medium?: string;
}

export default function MyLinks() {
  const { affiliate } = useAuthStore();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<SmartLink | null>(null);

  const { data: smartLinks, isLoading } = useQuery({
    queryKey: ['smart-links', affiliate?.id],
    queryFn: async () => {
      const response = await api.get(`/tracking/smartlinks?affiliateId=${affiliate?.id}`);
      return response.data.data as SmartLink[];
    },
    enabled: !!affiliate?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (data: SmartLinkFormData) => {
      const response = await api.post('/tracking/smartlinks', {
        ...data,
        affiliateId: affiliate?.id,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smart-links'] });
      toast.success('Smart link created successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create smart link');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SmartLinkFormData }) => {
      const response = await api.put(`/tracking/smartlinks/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smart-links'] });
      toast.success('Smart link updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update smart link');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tracking/smartlinks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smart-links'] });
      toast.success('Smart link deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete smart link');
    },
  });

  const handleSubmit = async (data: SmartLinkFormData) => {
    if (editingLink) {
      await updateMutation.mutateAsync({ id: editingLink.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    setEditingLink(null);
  };

  const handleEdit = (link: SmartLink) => {
    setEditingLink(link);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLink(null);
  };

  const getSmartLinkUrl = (shortCode: string) => {
    return `https://app.worldreach.io/go/${shortCode}`;
  };

  const referralUrl = affiliate?.referralCode
    ? `https://app.worldreach.io/register?ref=${affiliate.referralCode}`
    : '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">My Links</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Manage your referral and smart tracking links
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5" />
          Create Smart Link
        </button>
      </div>

      {/* Main Referral Link */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Primary Referral Link</h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Your main referral link with your unique code. Use this for general promotion.
            </p>
          </div>
          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-0.5 text-sm font-medium text-green-800">
            Always Active
          </span>
        </div>

        <div className="mt-4 flex items-center gap-3 bg-[var(--bg-secondary)] rounded-lg p-4">
          <LinkIcon className="h-5 w-5 text-[var(--text-muted)] flex-shrink-0" />
          <code className="flex-1 text-sm font-mono text-[var(--text-secondary)] truncate">
            {referralUrl || 'Loading...'}
          </code>
          {referralUrl && (
            <CopyButton text={referralUrl} label="Copy" />
          )}
        </div>

        <div className="mt-4 flex items-center gap-6 text-sm">
          <span className="text-[var(--text-muted)]">
            Referral Code: <span className="font-mono font-semibold text-[var(--text-primary)]">{affiliate?.referralCode}</span>
          </span>
        </div>
      </div>

      {/* Smart Links Table */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--border-color)]">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Smart Links</h2>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Create custom tracking links for specific campaigns and traffic sources.
          </p>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-[var(--text-muted)]">Loading links...</p>
          </div>
        ) : !smartLinks?.length ? (
          <div className="p-8 text-center">
            <LinkIcon className="mx-auto h-12 w-12 text-[var(--text-muted)]" />
            <h3 className="mt-2 text-sm font-medium text-[var(--text-primary)]">No smart links</h3>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Create your first smart link to track specific campaigns.
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              <PlusIcon className="h-5 w-5" />
              Create Smart Link
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[var(--bg-secondary)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Link
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Clicks
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Conversions
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[var(--bg-secondary)] divide-y divide-gray-200">
                {smartLinks.map((link) => (
                  <tr key={link.id} className="hover:bg-[var(--bg-secondary)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[var(--text-primary)]">{link.name}</div>
                      <div className="text-xs text-[var(--text-muted)]">
                        Created {new Date(link.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono text-[var(--text-secondary)] max-w-[200px] truncate">
                          {getSmartLinkUrl(link.shortCode)}
                        </code>
                        <CopyButton text={getSmartLinkUrl(link.shortCode)} size="sm" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[var(--text-primary)]">{link.campaign || '-'}</div>
                      {(link.source || link.medium) && (
                        <div className="text-xs text-[var(--text-muted)]">
                          {[link.source, link.medium].filter(Boolean).join(' / ')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1 text-sm text-[var(--text-primary)]">
                        <ChartBarIcon className="h-4 w-4 text-[var(--text-muted)]" />
                        {link.clicks.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-[var(--text-primary)]">{link.conversions.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                          link.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]'
                        }`}
                      >
                        {link.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(link)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-primary-600 rounded-md hover:bg-[var(--bg-tertiary)]"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(link.id)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-red-600 rounded-md hover:bg-[var(--bg-tertiary)]"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SmartLinkModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        editData={editingLink}
        loading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
